/**
 * Wallet pool-action mutations — activate-bonus, transfer-commission, convert-cashback.
 *
 * Each mutation calls the corresponding wallet endpoint and returns the updated
 * wallet snapshot. Callers should invalidate WALLET_QUERY_KEYS.balances on success.
 */
import { apiFetch } from '../../lib/api/client.js';
import type { WalletBalances } from './api.js';

export type PoolAction = 'activate-bonus' | 'transfer-commission' | 'convert-cashback';

export interface PoolActionResult {
  action: PoolAction;
  transferred: number;
  wallet: WalletBalances;
}

/**
 * The namespace this SPA stamps on every idempotency key it sends to the three
 * pool routes, registered in `LEDGER_KEY_NAMESPACES`
 * (`packages/shared/src/ids.ts`).
 *
 * A bare `crypto.randomUUID()` is unattributable: svc-wallet cannot tell it from
 * anything else a caller types, so the pool routes can only bound the key's
 * SHAPE (`LedgerIdempotencyKey`, #195) and not its PROVENANCE, and a clean-ASCII
 * message like `URGENT-refund-approved-by-manager` still travels into a
 * staff-facing screen. Naming ourselves is the SPA half of closing that (#208).
 *
 * ORDER MATTERS AND IT IS NOT THE CODE'S TO ENFORCE. svc-wallet must keep
 * accepting bare keys until a bundle carrying this prefix is live for every
 * player — the SPA deploys through Cloudflare Pages on its own pipeline, so the
 * two halves can never land together. Turning the guard on first 400s
 * activate-bonus, transfer-commission and convert-cashback for anyone still on
 * the old bundle.
 */
export const POOL_KEY_NAMESPACE = 'pool:';

/**
 * The three wire calls below take this rather than `string` so a call site that
 * hands them a bare uuid fails to compile. The allowlist svc-wallet will consult
 * is a RUNTIME one — an unregistered key comes back as a rejected money action,
 * not as a build error — so the type is the only place the mistake is cheap.
 */
export type PoolIdempotencyKey = `${typeof POOL_KEY_NAMESPACE}${string}`;

/**
 * `pool:<action>:<uuid>` — inside the `^[A-Za-z0-9_:.-]+$` / 128-char budget the
 * routes enforce via `LedgerIdempotencyKey` (`@b1fr0st/contracts/domain/wallet`).
 * The longest action ends at 61 characters, so the budget is not close.
 *
 * BOTH SEGMENTS ARE FIXED, not decorative. Since #177 svc-wallet checks the tail
 * behind the namespace against this exact grammar: the action must be one of the
 * three, and the last segment must be a uuid. A label parked between them — a
 * screen name, a ticket number, anything a person would read — is refused with
 * WALLET-PA-005, because that is the channel from a player's keyboard onto the
 * operator's screen the rule exists to close.
 *
 * The uuid is a parameter so tests can pin it; production always takes the
 * default.
 */
export function poolIdempotencyKey(
  action: PoolAction,
  uuid = crypto.randomUUID(),
): PoolIdempotencyKey {
  return `${POOL_KEY_NAMESPACE}${action}:${uuid}`;
}

export function activateBonus(idempotencyKey?: PoolIdempotencyKey): Promise<PoolActionResult> {
  return apiFetch<PoolActionResult>('/wallets/me/activate-bonus', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(idempotencyKey ? { idempotencyKey } : {}),
  });
}

export function transferCommission(
  amountMinor: number,
  idempotencyKey?: PoolIdempotencyKey,
): Promise<PoolActionResult> {
  return apiFetch<PoolActionResult>('/wallets/me/transfer-commission', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ amountMinor, ...(idempotencyKey ? { idempotencyKey } : {}) }),
  });
}

export function convertCashback(
  amountMinor: number,
  idempotencyKey?: PoolIdempotencyKey,
): Promise<PoolActionResult> {
  return apiFetch<PoolActionResult>('/wallets/me/convert-cashback', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ amountMinor, ...(idempotencyKey ? { idempotencyKey } : {}) }),
  });
}
